import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Account, type Movement, type Usage, type UsageDay } from '../api';
import { formatCredits, formatDate } from '../format';
import { readRuntimeConfigValue } from '../runtime-config';
import { Empty, PageHead, TableWrap } from '../ui';

const amounts = [100, 500, 1000, 2500, 5000, 10000, 20000];
const providerLabels: Record<string, string> = {
  platega: 'Card / SBP',
  platega_eur: 'Card (EUR)',
  nowpayments: 'Crypto',
};

// Older deployments answer without the field; card is what every one of them
// has, and it is the option that works for an account with no bot.
const defaultProviders = ['platega'];

// Set when the browser leaves for a provider's checkout, read when it comes
// back. Without it the return landed on a page showing the old balance and no
// hint that anything was in flight — which reads as a payment that vanished.
const PENDING_TOPUP = 'imaginai.pending-topup';

// One bar per day, tallest bar being the busiest day in the window.
//
// A table of thirty rows — most of them zero for anybody who does not generate
// daily — buried everything below it. This says the same thing in a glance and
// still shows the quiet days, because a missing day reads as missing data.
function SpendChart({ days }: { days: UsageDay[] }) {
  // A window with no spend at all drew an axis reading 1 / 1 / 0 over an empty
  // grid. Saying so is shorter and true.
  if (days.every((day) => day.spent + day.refunded === 0)) {
    return <Empty title="Nothing spent in this window." />;
  }
  const peak = Math.max(...days.map((day) => day.spent + day.refunded), 1);
  // Round the top of the scale so the axis reads 0 / 250 / 500 rather than
  // 0 / 173 / 347 — the number a reader compares against should be a number
  // they would have chosen.
  const step = Math.pow(10, Math.floor(Math.log10(peak)));
  const top = Math.ceil(peak / step) * step;

  return (
    <>
      <div className="chart">
        <div className="chart-axis" aria-hidden="true">
          <span>{formatCredits(top)}</span>
          <span>{formatCredits(Math.round(top / 2))}</span>
          <span>0</span>
        </div>
        <div className="chart-grid" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="days">
          {days.map((day) => {
            const quiet = day.spent === 0 && day.refunded === 0;
            return (
              <div
                key={day.date}
                className={quiet ? 'day quiet' : 'day'}
                title={`${day.date}: spent ${day.spent}, refunded ${day.refunded}, bought ${day.topped_up}`}
              >
                {day.refunded > 0 ? (
                  <div className="bar refunded" style={{ height: `${(day.refunded / top) * 100}%` }} />
                ) : null}
                <div
                  className="bar"
                  style={{ height: quiet ? '2px' : `${Math.max(2, (day.spent / top) * 100)}%` }}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className="days-legend">
        <span>
          <i className="swatch" style={{ background: 'var(--accent)' }} /> spent
        </span>
        <span>
          <i className="swatch" style={{ background: 'var(--danger)' }} /> refunded
        </span>
        <span>
          {days[0].date} — {days[days.length - 1].date}
        </span>
      </div>
    </>
  );
}

export default function Overview({
  account,
  onChanged,
}: {
  account: Account;
  onChanged: () => void;
}) {
  const [windowDays, setWindowDays] = useState(30);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [keyCount, setKeyCount] = useState<number | null>(null);
  // An empty list is a deployment with no merchant credentials, which is a
  // different thing from "not loaded yet": the field is absent on an older
  // build, present and empty when payment is genuinely unavailable.
  const providers = account.topup_providers ?? defaultProviders;
  // Configured on every deployment and read by nothing until now, so "write to
  // support" named nobody to write to.
  const supportURL = readRuntimeConfigValue('VITE_SUPPORT_TELEGRAM_URL');
  const [provider, setProvider] = useState(providers[0] ?? '');
  const [amount, setAmount] = useState(amounts[2]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [returned, setReturned] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [nextUsage, ledger, keys] = await Promise.all([
        api.usage(windowDays),
        api.ledger(windowDays),
        api.keys(),
      ]);
      setUsage(nextUsage);
      setMovements(ledger.items);
      setKeyCount(keys.items.filter((key) => !key.revoked).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load usage');
    }
  }, [windowDays]);

  useEffect(() => {
    void load();
  }, [load]);

  // Coming back from a checkout: say what is happening, and go and look again
  // rather than making the person reload the page to find out.
  useEffect(() => {
    if (!sessionStorage.getItem(PENDING_TOPUP)) return;
    sessionStorage.removeItem(PENDING_TOPUP);
    setReturned(true);
    const timers = [4000, 12000].map((delay) => setTimeout(() => void load(), delay));
    return () => timers.forEach(clearTimeout);
  }, [load]);

  const buy = async () => {
    setBusy(true);
    setError('');
    try {
      const topUp = await api.topUp(provider, amount);
      sessionStorage.setItem(PENDING_TOPUP, String(amount));
      window.location.href = topUp.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the payment');
      setBusy(false);
    }
  };

  const balance = usage ? usage.remaining : account.balance;
  // Days with spend, not days in the window: somebody who runs a batch on
  // Mondays is not spending a seventh of it every day, and dividing by 30 tells
  // them they have a year of credits left.
  const activeDays = usage?.days?.filter((day) => day.spent > 0) ?? [];
  const perActiveDay = activeDays.length
    ? Math.round(activeDays.reduce((sum, day) => sum + day.spent, 0) / activeDays.length)
    : 0;
  const daysLeft = perActiveDay > 0 ? Math.floor(balance / perActiveDay) : null;
  const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

  return (
    <div className="overview">
      <PageHead
        title="Overview"
        lead="What the account has, what it spent, and where the credits went."
      />

      {account.status === 'SUSPENDED' ? (
        <div className="notice bad">
          This account is suspended, so its keys are refused. The keys themselves are intact — get
          in touch and it can be switched back on.
        </div>
      ) : null}

      {returned ? (
        <div className="notice good">
          Back from the checkout. Credits land within a minute of the provider confirming; this page
          is checking again on its own. Every payment shows in the statement below, settled or not.
        </div>
      ) : null}

      {keyCount === 0 ? (
        <div className="notice">
          <p>
            <strong>No keys yet.</strong> The API refuses every request until there is one.
          </p>
          <p className="small">
            <Link to="/keys">Create a key</Link>, then the four calls worth knowing are on the{' '}
            <Link to="/docs">Docs</Link> page.
          </p>
        </div>
      ) : null}

      {daysLeft !== null && daysLeft <= 3 && balance > 0 ? (
        <div className="notice">
          At the rate of your recent days this balance covers about {daysLeft}{' '}
          {plural(daysLeft, 'more day', 'more days')} of work. A generation that meets an empty
          balance is refused with 402 rather than queued.
        </div>
      ) : null}

      <div className="stats">
        <div className="stat primary">
          <div className="label">Balance</div>
          <div className="value">{formatCredits(balance)}</div>
          <div className="hint">
            {perActiveDay > 0
              ? `≈ ${daysLeft} ${plural(daysLeft ?? 0, 'day', 'days')} at ${formatCredits(perActiveDay)}/day`
              : 'credits'}
          </div>
        </div>
        <div className="stat">
          <div className="label">Spent · {windowDays}d</div>
          <div className="value">{usage ? formatCredits(usage.spent) : '—'}</div>
          <div className="hint">
            {activeDays.length} {plural(activeDays.length, 'active day', 'active days')}
          </div>
        </div>
        <div className="stat">
          <div className="label">Refunded · {windowDays}d</div>
          <div className="value">{usage ? formatCredits(usage.refunded) : '—'}</div>
          <div className="hint">failed or rejected</div>
        </div>
        <div className="stat">
          <div className="label">Limits</div>
          <div className="value">
            {account.concurrent_limit} / {account.rps_limit}
          </div>
          <div className="hint">in flight / per second</div>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="panel">
        <h3>Buy credits</h3>
        {providers.length === 0 ? (
          <p className="muted">
            Card payment is not configured on this deployment yet.{' '}
            {supportURL ? (
              <a href={supportURL} target="_blank" rel="noreferrer">
                Write to support
              </a>
            ) : (
              'Write to support'
            )}{' '}
            and we will credit the account by hand.
          </p>
        ) : (
        <div className="row">
          <div>
            <label htmlFor="amount">Credits</label>
            <select
              id="amount"
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
            >
              {amounts.map((value) => (
                <option key={value} value={value}>
                  {formatCredits(value)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="provider">Pay with</label>
            <select
              id="provider"
              value={provider}
              onChange={(event) => setProvider(event.target.value)}
            >
              {providers.map((item) => (
                <option key={item} value={item}>
                  {providerLabels[item] ?? item}
                </option>
              ))}
            </select>
          </div>
          <button onClick={buy} disabled={busy}>
            {busy ? 'Opening checkout…' : 'Continue to payment'}
          </button>
        </div>
        )}
        <p className="panel-note">
          Credits land on the API balance. They are separate from any balance you have in the bot.
        </p>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Spend by day</h3>
          <span className="muted small">UTC days · last {windowDays}</span>
        </div>
        <SpendChart days={usage?.days ?? []} />
        <p className="panel-note">
          A quiet day is part of the series rather than a gap — the same window your invoice covers.
        </p>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Statement</h3>
          <div className="row">
            <select
              aria-label="Window"
              value={windowDays}
              onChange={(event) => setWindowDays(Number(event.target.value))}
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
            {/* Reloads the statement as well as the account. It used to call
                only the account refresh, so the button under the table it was
                sitting on left that table exactly as it was. */}
            <button
              className="secondary"
              onClick={() => {
                onChanged();
                void load();
              }}
            >
              Refresh
            </button>
          </div>
        </div>
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th className="num">Credits</th>
                <th>Reason</th>
                <th className="optional">Reference</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((movement) => (
                <tr key={movement.id}>
                  <td className="small">{formatDate(movement.created_at)}</td>
                  <td className={`num ${movement.delta < 0 ? 'danger' : 'ok'}`}>
                    {movement.delta > 0 ? '+' : '−'}
                    {formatCredits(Math.abs(movement.delta))}
                  </td>
                  <td className="small">{movement.reason.toLowerCase()}</td>
                  <td className="small mono optional">{movement.reference ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        {movements.length === 0 ? (
          <Empty title={`Nothing in the last ${windowDays} days.`}>
            Top-ups and generations both appear here, newest first.
          </Empty>
        ) : null}
        <p className="panel-note">
          Every line is a movement. A refund means a generation failed and its credits came back —
          it is reported apart from spend rather than netted against it.
        </p>
      </div>
    </div>
  );
}
